import { ICONS } from "../../../constants/icons"

/** Permission matrix columns — Figma Create Role (`40005253:75762`). */

export const PERMISSION_COLUMNS = [
  { key: "none", label: "No Access" },
  { key: "view", label: "View Only" },
  { key: "create", label: "Create" },
  { key: "edit", label: "Edit" },
  { key: "delete", label: "Delete" },
  { key: "full", label: "Full Access" },
]

export const PERMISSION_LEVEL_KEYS = PERMISSION_COLUMNS.map(column => column.key)

export const EMPTY_PERMISSION_SELECTION = {
  none: true,
  view: false,
  create: false,
  edit: false,
  delete: false,
  full: false,
}

const CHILD_LEVEL_KEYS = ["view", "create", "edit", "delete"]

/** Create / Edit / Delete require View — auto-bind when selected. */
const VIEW_DEPENDENT_LEVELS = ["create", "edit", "delete"]

/** @deprecated Static Figma modules — prefer mapPermissionGroupsToModules */
export const PERMISSION_MODULES = [
  {
    id: "dashboard",
    label: "Dashboard",
    icon: ICONS.adminDashboard,
    iconTone: "purple",
  },
  {
    id: "members-staff",
    label: "Members & Staff",
    icon: ICONS.adminMembers,
    iconTone: "orange",
    defaultExpanded: true,
    screens: [
      { id: "members-listing", label: "Members Listing" },
      { id: "member-detail", label: "Member Detail" },
      { id: "add-member", label: "Add Member" },
      { id: "staff-management", label: "Staff Management" },
    ],
  },
  {
    id: "projects-sessions",
    label: "Projects & Sessions",
    icon: ICONS.adminProjects,
    iconTone: "blue",
  },
  {
    id: "cities",
    label: "Cities",
    icon: ICONS.adminCities,
    iconTone: "yellow",
  },
  {
    id: "roles-permissions",
    label: "Roles & Permissions",
    icon: ICONS.adminRoles,
    iconTone: "red",
  },
  {
    id: "requests-flags",
    label: "Requests & Flags",
    icon: ICONS.adminRequests,
    iconTone: "green",
  },
  {
    id: "settings",
    label: "Settings & Configuration",
    icon: ICONS.adminSettings,
    iconTone: "orange",
  },
]

/** Figma tag chip colors — Create Role permissions (`40005253:75762`). */
export const MODULE_ICON_TONES = {
  purple: { bg: "#f9eeff", icon: "#7200b0" },
  orange: { bg: "#fff4ed", icon: "#da6223" },
  blue: { bg: "#ecf0fa", icon: "#1e5ad2" },
  red: { bg: "#ffdfdf", icon: "#9b0000" },
  green: { bg: "#edf8eb", icon: "#46a631" },
  yellow: { bg: "#faf5ca", icon: "#7a5c00" },
  neutral: { bg: "#f5f5f5", icon: "#6e6e6e" },
}

const TYPE_ICON_TONE_MAP = {
  invites: "orange",
  roles: "red",
  permission: "red",
  permissions: "red",
  "collaborator-limits": "green",
  "city-update-requests": "yellow",
  cities: "yellow",
  "role-types": "red",
  spellbooks: "purple",
  "role-classes": "purple",
  staff: "orange",
  calls: "green",
  blogs: "green",
  photography: "blue",
  "photography-collaborator": "blue",
  flags: "green",
  projects: "blue",
  rewards: "purple",
  "reward-types": "purple",
  "reward-claims": "purple",
  "review-writer": "green",
  reviews: "green",
  sessions: "orange",
  "activity-logs": "blue",
  members: "orange",
}

const TYPE_ICON_MAP = {
  invites: ICONS.adminMembers,
  roles: ICONS.adminRoles,
  permission: ICONS.adminRoles,
  permissions: ICONS.adminRoles,
  "collaborator-limits": ICONS.adminRequests,
  "city-update-requests": ICONS.adminCities,
  cities: ICONS.adminCities,
  "role-types": ICONS.adminRoles,
  spellbooks: ICONS.adminArtsGenome,
  "role-classes": ICONS.adminArtsGenome,
  staff: ICONS.adminStaff,
  calls: ICONS.adminInbox,
  blogs: ICONS.adminBlogs,
  photography: ICONS.adminPhotography,
  "photography-collaborator": ICONS.adminPhotography,
  flags: ICONS.adminFlags,
  projects: ICONS.adminProjects,
  rewards: ICONS.adminRewards,
  "reward-types": ICONS.adminRewards,
  "reward-claims": ICONS.adminRewards,
  "review-writer": ICONS.adminBlogs,
  reviews: ICONS.adminBlogs,
  sessions: ICONS.adminSessions,
  "activity-logs": ICONS.adminActivityLog,
  members: ICONS.adminMembers,
}

const getActionFromPermissionName = name => {
  const raw = String(name || "")
  const parts = raw.split(".")
  const action = parts[parts.length - 1]?.toLowerCase() || ""
  if (action === "all") return "full"
  if (action === "read") return "view"
  if (action === "update") return "edit"
  if (["create", "delete", "view", "edit", "full"].includes(action)) {
    return action
  }
  // parent without .all suffix (e.g. "photography")
  if (!raw.includes(".") || action === raw.toLowerCase()) return "full"
  return null
}

const resolveIcon = type =>
  TYPE_ICON_MAP[String(type || "").toLowerCase()] || ICONS.adminGrid4

export const resolveModuleIconTone = type => {
  const key = String(type || "").toLowerCase()
  return TYPE_ICON_TONE_MAP[key] || "neutral"
}

/**
 * Map GET /api/v1/admin/permissions/get-all → matrix modules.
 */
export const mapPermissionGroupsToModules = (groups = []) =>
  (Array.isArray(groups) ? groups : []).map(group => {
    const children = Array.isArray(group.children) ? group.children : []
    const actionIds = {
      full: group.id,
      view: null,
      create: null,
      edit: null,
      delete: null,
    }

    children.forEach(child => {
      const action = getActionFromPermissionName(child.name)
      if (action && action !== "full" && actionIds[action] === null) {
        actionIds[action] = child.id
      }
    })

    // Parent name may itself be the "all" permission
    const parentAction = getActionFromPermissionName(group.name)
    if (parentAction === "full") {
      actionIds.full = group.id
    }

    return {
      id: group.id,
      label: group.description || group.name || "Permission",
      icon: resolveIcon(group.type),
      iconTone: resolveModuleIconTone(group.type),
      type: group.type,
      name: group.name,
      actionIds,
      availableLevels: [
        "none",
        ...PERMISSION_COLUMNS.map(col => col.key).filter(
          key => key !== "none" && actionIds[key],
        ),
      ],
    }
  })

export const buildDefaultPermissionsFromModules = (modules = []) => {
  const permissions = {}
  modules.forEach(module => {
    permissions[module.id] = { ...EMPTY_PERMISSION_SELECTION }
  })
  return permissions
}

export const buildFullPermissionSelection = (availableLevels = []) => {
  const available = new Set(availableLevels)
  const selection = { ...EMPTY_PERMISSION_SELECTION, none: false, full: true }
  CHILD_LEVEL_KEYS.forEach(key => {
    if (available.has(key)) selection[key] = true
  })
  return selection
}

export const normalizePermissionSelection = (value, availableLevels = []) => {
  if (value && typeof value === "object" && "none" in value) {
    return { ...EMPTY_PERMISSION_SELECTION, ...value }
  }
  if (typeof value === "string") {
    if (value === "none") return { ...EMPTY_PERMISSION_SELECTION }
    if (value === "full") return buildFullPermissionSelection(availableLevels)
    if (CHILD_LEVEL_KEYS.includes(value)) {
      return {
        ...EMPTY_PERMISSION_SELECTION,
        none: false,
        [value]: true,
      }
    }
  }
  return { ...EMPTY_PERMISSION_SELECTION }
}

/**
 * Toggle one permission cell for a module row (multi-select with none/full rules).
 * Create / Edit / Delete auto-select View (they depend on it). Unchecking View
 * clears Create / Edit / Delete.
 */
export const applyPermissionToggle = (
  current,
  level,
  availableLevels = PERMISSION_LEVEL_KEYS,
) => {
  const available = new Set(availableLevels)
  const selection = normalizePermissionSelection(current, availableLevels)

  if (level === "none") {
    return { ...EMPTY_PERMISSION_SELECTION }
  }

  if (level === "full") {
    if (!available.has("full")) return selection
    if (selection.full) {
      const next = {
        ...selection,
        full: false,
      }
      const hasChild = CHILD_LEVEL_KEYS.some(
        key => available.has(key) && next[key],
      )
      return hasChild ? next : { ...EMPTY_PERMISSION_SELECTION }
    }
    return buildFullPermissionSelection(availableLevels)
  }

  if (!available.has(level)) return selection

  const next = {
    ...selection,
    none: false,
    full: false,
    [level]: !selection[level],
  }

  // Selecting Create / Edit / Delete always includes View.
  if (VIEW_DEPENDENT_LEVELS.includes(level) && next[level] && available.has("view")) {
    next.view = true
  }

  // Clearing View also clears dependent Create / Edit / Delete.
  if (level === "view" && !next.view) {
    VIEW_DEPENDENT_LEVELS.forEach(key => {
      if (available.has(key)) next[key] = false
    })
  }

  const childKeys = CHILD_LEVEL_KEYS.filter(key => available.has(key))
  const allChildrenSelected =
    childKeys.length > 0 && childKeys.every(key => next[key])

  if (allChildrenSelected && available.has("full")) {
    return buildFullPermissionSelection(availableLevels)
  }

  const anySelected = childKeys.some(key => next[key])
  if (!anySelected) {
    return { ...EMPTY_PERMISSION_SELECTION }
  }

  return next
}

export const isPermissionUnavailable = ({
  columnKey,
  availableLevels,
}) => {
  if (columnKey === "none" || !availableLevels) return false
  return !availableLevels.includes(columnKey)
}

export const isPermissionCellDisabled = ({
  columnKey,
  selection,
  availableLevels = PERMISSION_LEVEL_KEYS,
  readOnly = false,
}) => {
  if (
    isPermissionUnavailable({
      columnKey,
      availableLevels,
    })
  ) {
    return false
  }

  const normalized = normalizePermissionSelection(selection, availableLevels)

  if (readOnly) return true
  if (
    normalized.full &&
    columnKey !== "none" &&
    columnKey !== "full" &&
    CHILD_LEVEL_KEYS.includes(columnKey)
  ) {
    return true
  }
  return false
}

export const buildDefaultPermissions = () =>
  buildDefaultPermissionsFromModules(PERMISSION_MODULES)

/**
 * Derive matrix selections from selected permission IDs.
 */
export const mapSelectedIdsToPermissionSelections = (
  modules = [],
  selectedIds = [],
) => {
  const selected = new Set((selectedIds || []).filter(Boolean))
  const selections = {}

  modules.forEach(module => {
    const availableLevels = module.availableLevels || PERMISSION_LEVEL_KEYS
    const { actionIds } = module

    if (actionIds.full && selected.has(actionIds.full)) {
      selections[module.id] = buildFullPermissionSelection(availableLevels)
      return
    }

    const selection = { ...EMPTY_PERMISSION_SELECTION, none: false }
    let any = false

    CHILD_LEVEL_KEYS.forEach(key => {
      if (actionIds[key] && selected.has(actionIds[key])) {
        selection[key] = true
        any = true
      }
    })

    // Create / Edit / Delete imply View when the module offers it.
    if (
      availableLevels.includes("view") &&
      VIEW_DEPENDENT_LEVELS.some(key => selection[key])
    ) {
      selection.view = true
      any = true
    }

    if (!any) {
      selections[module.id] = { ...EMPTY_PERMISSION_SELECTION }
      return
    }

    const childKeys = CHILD_LEVEL_KEYS.filter(key =>
      availableLevels.includes(key),
    )
    if (childKeys.length > 0 && childKeys.every(key => selection[key])) {
      selections[module.id] = buildFullPermissionSelection(availableLevels)
      return
    }

    selections[module.id] = selection
  })

  return selections
}

/** @deprecated — use mapSelectedIdsToPermissionSelections */
export const mapSelectedIdsToLevels = (modules = [], selectedIds = []) => {
  const selections = mapSelectedIdsToPermissionSelections(modules, selectedIds)
  const levels = {}
  modules.forEach(module => {
    const selection = selections[module.id] || EMPTY_PERMISSION_SELECTION
    if (selection.full) {
      levels[module.id] = "full"
      return
    }
    if (selection.none) {
      levels[module.id] = "none"
      return
    }
    const picked = CHILD_LEVEL_KEYS.find(key => selection[key])
    levels[module.id] = picked || "none"
  })
  return levels
}

/**
 * Collect all permission IDs for a module when Full Access is selected.
 * Backend expects parent `.all` id plus every child permission id.
 */
const collectFullAccessPermissionIds = (actionIds = {}) => {
  const ids = []
  if (actionIds.full) ids.push(actionIds.full)
  CHILD_LEVEL_KEYS.forEach(key => {
    if (actionIds[key]) ids.push(actionIds[key])
  })
  return ids
}

/**
 * Convert matrix selections → permission ID list for create/update payload.
 * Full Access sends parent `.all` id and all child permission ids.
 */
export const mapPermissionSelectionsToIds = (modules = [], selections = {}) => {
  const ids = []

  modules.forEach(module => {
    const selection = normalizePermissionSelection(
      selections[module.id],
      module.availableLevels,
    )

    if (
      selection.none &&
      !selection.view &&
      !selection.create &&
      !selection.edit &&
      !selection.delete &&
      !selection.full
    ) {
      return
    }

    if (selection.full && module.actionIds?.full) {
      ids.push(...collectFullAccessPermissionIds(module.actionIds))
      return
    }

    const hasDependent = VIEW_DEPENDENT_LEVELS.some(key => selection[key])
    if (hasDependent && module.actionIds?.view) {
      ids.push(module.actionIds.view)
    }

    CHILD_LEVEL_KEYS.forEach(key => {
      if (selection[key] && module.actionIds[key]) {
        ids.push(module.actionIds[key])
      }
    })
  })

  return [...new Set(ids)]
}

/** @deprecated — use mapPermissionSelectionsToIds */
export const mapLevelsToPermissionIds = (modules = [], levels = {}) => {
  const selections = {}
  modules.forEach(module => {
    selections[module.id] = normalizePermissionSelection(
      levels[module.id],
      module.availableLevels,
    )
  })
  return mapPermissionSelectionsToIds(modules, selections)
}
